import { useCallback } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Link } from '@amzn/stencil-react-components/link';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
// standards-ignore: cross-feature — IconBinSmall/IconExternalLinkSmall are generic remove/external-link affordances; a shared wrapper over a bare icon would add indirection without value
import { IconBinSmall, IconExternalLinkSmall } from '@amzn/stencil-react-icons';
import type { DocLink } from 'src/types/helios/Question';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

import { truncateStyle } from '../truncateStyle';
import { isValidDocLinkUrl } from './docLinkValidation';

/** Column flex proportions shared by the header and every row so the
 *  Document / Link / Actions columns stay aligned. */
const COL_DOCUMENT_FLEX = 2;
const COL_LINK_FLEX = 3;
const COL_ACTIONS_FLEX = 1;

/** Single-line ellipsis clamp for a cell's text so a long value never wraps or
 *  blows out its column. Module-level for reference stability. */
const SINGLE_LINE = truncateStyle(1);

/** Bordered container around the links table (headers + rows / empty state),
 *  matching the mock's boxed Document / Link / Actions grid. Token-based
 *  border + radius (a raw `var(--st-dt-...)` string doesn't resolve in every
 *  render context). Module-level for emotion reference stability. */
const TABLE_BOX_STYLE = {
  border: `${token('dimensions.border.width.100')} solid ${token('color.border.primary')}`,
  borderRadius: token('dimensions.border.radius.100'),
} as const;

/**
 * One documentation reference: an optional display title and its URL. Reuses
 * the Helios `DocLink` contract so this field edits the real question shape
 * (single source of truth) rather than a forked copy.
 */
export type DocLinkItem = DocLink;

/** Visible copy — supplied by the caller so this stays i18n-bundle-agnostic
 *  and reusable across features. */
export interface DocLinksFieldLabels {
  /** Column header above the title column. */
  readonly documentColumnHeader: string;
  /** Column header above the link column. */
  readonly linkColumnHeader: string;
  /** Column header above the actions (delete) column. */
  readonly actionsColumnHeader: string;
  /** Accessible label prefix for each row's icon-only delete button (followed
   *  by the link's title/URL). */
  readonly removeAltText: string;
  readonly emptyText: string;
}

export interface DocLinksFieldProps {
  /** Current list of doc links. */
  readonly value: readonly DocLinkItem[];
  /** Emits the whole next list on remove. */
  readonly onChange: (next: DocLinkItem[]) => void;
  readonly labels: DocLinksFieldLabels;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * Read-and-remove documentation-link table: a bordered box with Document /
 * Link / Actions column headers, one row per link (title, clickable URL, and
 * an icon-only delete), and a centered empty state. Adding is owned by the
 * caller's "Add Document Link" modal, which appends to `value` — this field
 * only renders the list and emits the next array on removal.
 *
 * Fully controlled: the owner holds the list and this emits the whole next
 * array on every remove.
 */
export function DocLinksField({
  value,
  onChange,
  labels,
  disabled = false,
  dataTestId,
}: DocLinksFieldProps): JSX.Element {
  const handleRemove = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  return (
    <Col
      gridGap="dimensions.spacing.100"
      padding="dimensions.spacing.300"
      css={TABLE_BOX_STYLE}
      width="100%"
    >
      {/* Column header — aligns with each row's Document / Link / Actions
       *  columns. */}
      <Row gridGap="dimensions.spacing.300" alignItems="center">
        <Col flex={COL_DOCUMENT_FLEX} minWidth={0}>
          <Text fontWeight="bold" color="color.text.secondary" fontSize="T100">
            {labels.documentColumnHeader}
          </Text>
        </Col>
        <Col flex={COL_LINK_FLEX} minWidth={0}>
          <Text fontWeight="bold" color="color.text.secondary" fontSize="T100">
            {labels.linkColumnHeader}
          </Text>
        </Col>
        <Col flex={COL_ACTIONS_FLEX} minWidth={0}>
          <Text fontWeight="bold" color="color.text.secondary" fontSize="T100">
            {labels.actionsColumnHeader}
          </Text>
        </Col>
      </Row>

      {value.length === 0 ? (
        <Row justifyContent="center" padding={{ top: 'dimensions.spacing.300' }}>
          <Text
            color="color.text.secondary"
            fontWeight="bold"
            data-testid={dataTestId ? `${dataTestId}-empty` : undefined}
          >
            {labels.emptyText}
          </Text>
        </Row>
      ) : (
        value.map((item, index) => (
          <Row
            // eslint-disable-next-line @eslint-react/no-array-index-key -- the list never reorders and removal is already index-based (handleRemove(index)); an index key is collision-proof even for caller-supplied data with duplicate/empty links, where link/title-based keys would collide
            key={index}
            alignItems="center"
            gridGap="dimensions.spacing.300"
            data-testid={dataTestId ? `${dataTestId}-row-${index}` : undefined}
          >
            {/* Document column — the title (or a dash when untitled). */}
            <Col flex={COL_DOCUMENT_FLEX} minWidth={0}>
              <Text css={SINGLE_LINE}>{item.title ?? '—'}</Text>
            </Col>
            {/* Link column — the URL as a new-tab link + external glyph,
             *  clamped to one line so it never wraps under the title. */}
            <Col flex={COL_LINK_FLEX} minWidth={0}>
              {item.link && isValidDocLinkUrl(item.link) ? (
                // Safe scheme → clickable link.
                <Row alignItems="center" gridGap="dimensions.spacing.50" minWidth={0}>
                  <Link
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    css={SINGLE_LINE}
                  >
                    {item.link}
                  </Link>
                  <IconExternalLinkSmall aria-hidden="true" />
                </Row>
              ) : item.link ? (
                // Present but a disallowed scheme (e.g. `javascript:`): the
                // add path validates typed URLs, but `value` items can arrive
                // from the agent / persisted data / tests and never pass
                // through it. Rendering such a value into an anchor `href`
                // would be a live XSS vector, so show it as inert text.
                <Text css={SINGLE_LINE}>{item.link}</Text>
              ) : null}
            </Col>
            {/* Actions column — an icon-only delete (trash), aligned to the
             *  row's end. The accessible name carries the intent. */}
            <Col flex={COL_ACTIONS_FLEX} minWidth={0} alignItems="flex-end">
              <Button
                size={ButtonSize.Small}
                variant={ButtonVariant.Tertiary}
                icon={<IconBinSmall aria-hidden="true" />}
                aria-label={`${labels.removeAltText} ${item.title ?? item.link ?? ''}`.trim()}
                disabled={disabled}
                onClick={() => handleRemove(index)}
                data-testid={dataTestId ? `${dataTestId}-remove-${index}` : undefined}
              />
            </Col>
          </Row>
        ))
      )}
    </Col>
  );
}
